import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, Pause, Play, Plus, Wifi, Cable } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { ActionDialog } from "@/components/ActionDialog";
import { formatDate } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { type EstatusContrato } from "./schemas";
import {
  useListContratos,
  useSuspenderContrato,
  useReactivarContrato,
} from "./api";
import { ContratoFormDialog } from "./ContratoFormDialog";
import { useListPlanes } from "@/features/planes/api";
// rc138 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

const ESTATUS_VARIANT: Record<EstatusContrato, "success" | "warning" | "destructive" | "secondary" | "default"> = {
  activo: "success",
  suspendido: "warning",
  cancelado: "destructive",
  error: "destructive",
  pendiente: "secondary",
};

/**
 * Tab "Contratos" del detalle de cliente.
 * Muestra los contratos del cliente actual + botón para crear uno nuevo pre-ligado.
 * F14.4: botones suspender/activar por fila.
 */
type Props = {
  clienteId: string;
  /** Para que el back en ContratoDetailPage vuelva al cliente con su nombre. */
  fromLabel?: string;
};

export function ClienteContratosTab({ clienteId, fromLabel }: Props) {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [actionContrato, setActionContrato] = useState<
    { id: string; codigo: string; action: "suspender" | "reactivar" } | null
  >(null);
  // rc79: incluir cancelados para ver el histórico de contratos del cliente
  // (sobre todo en clientes dados de baja, cuyos contratos quedan cancelados).
  const { data, isLoading } = useListContratos({ cliente_id: clienteId, size: 50, incluir_cancelados: true });
  const items = data?.items ?? [];

  // F108 — mapa plan_id → tipo_servicio para columna Tipo
  const { data: planesData } = useListPlanes({ page: 1, size: 200 });
  const planTipoMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of planesData?.items ?? []) m.set(p.id, p.tipo_servicio);
    return m;
  }, [planesData]);

  /** Navega al contrato dejando breadcrumb que apunte de vuelta al cliente. */
  function gotoContrato(id: string) {
    navigate(`/contratos/${id}`, {
      state: fromLabel
        ? { from: `/clientes/${clienteId}`, fromLabel }
        : undefined,
    });
  }

  const isMobile = useIsMobile();

  const tipoChip = (tipo: string | undefined) =>
    tipo === "wireless" ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800 ring-1 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-900/40">
        <Wifi size={10} /> Wireless
      </span>
    ) : tipo === "fiber" ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-800 ring-1 ring-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-900/40">
        <Cable size={10} /> Fibra
      </span>
    ) : (
      <span className="text-[10px] text-muted-foreground">—</span>
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {data ? `${data.total} ${data.total === 1 ? "contrato" : "contratos"} ligados` : "Cargando…"}
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus size={12} /> Nuevo contrato
        </Button>
      </div>

      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>Sin contratos. Este cliente aún no tiene servicios activos.</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((c) => {
              const canSuspend = c.estatus === "activo" || c.estatus === "pendiente";
              const canReactivate = c.estatus === "suspendido";
              return (
                <MobileCard
                  key={c.id}
                  onClick={() => gotoContrato(c.id)}
                  title={<span className="font-mono">{c.codigo}</span>}
                  subtitle={<>{tipoChip(planTipoMap.get(c.plan_id))} <span className="ml-1">· corte día {c.fecha_corte}</span></>}
                  right={<Badge variant={ESTATUS_VARIANT[c.estatus]}>{c.estatus}</Badge>}
                  actions={
                    canSuspend ? (
                      <Button size="sm" variant="outline" onClick={() => setActionContrato({ id: c.id, codigo: c.codigo, action: "suspender" })}>
                        <Pause size={12} /> Suspender
                      </Button>
                    ) : canReactivate ? (
                      <Button size="sm" onClick={() => setActionContrato({ id: c.id, codigo: c.codigo, action: "reactivar" })}>
                        <Play size={12} /> Activar
                      </Button>
                    ) : undefined
                  }
                >
                  <MobileMeta label="PPPoE" mono>{c.pppoe_user}</MobileMeta>
                  <MobileMeta label="IP" mono>{c.ip_asignada}</MobileMeta>
                  <MobileMeta label="Equipo">{c.equipo_modalidad === "comodato" ? "Comodato" : "Propiedad"}</MobileMeta>
                  <MobileMeta label="Alta">{formatDate(c.created_at)}</MobileMeta>
                </MobileCard>
              );
            })}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>PPPoE user</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Corte</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead>Equipo</TableHead>
              <TableHead>Alta</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={FileText}
                  title="Sin contratos"
                  description="Este cliente aún no tiene servicios activos."
                  action={
                    <Button size="sm" onClick={() => setCreateOpen(true)}>
                      <Plus size={14} /> Crear contrato
                    </Button>
                  }
                />
              </TableEmpty>
            ) : (
              items.map((c) => {
                const canSuspend = c.estatus === "activo" || c.estatus === "pendiente";
                const canReactivate = c.estatus === "suspendido";
                const tipo = planTipoMap.get(c.plan_id);
                return (
                  <TableRow key={c.id} className="cursor-pointer">
                    <TableCell
                      className="font-mono"
                      onClick={() => gotoContrato(c.id)}
                    >
                      {c.codigo}
                    </TableCell>
                    <TableCell onClick={() => gotoContrato(c.id)}>
                      {tipo === "wireless" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800 ring-1 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-900/40">
                          <Wifi size={10} /> Wireless
                        </span>
                      ) : tipo === "fiber" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-800 ring-1 ring-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-900/40">
                          <Cable size={10} /> Fibra
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="font-mono"
                      onClick={() => gotoContrato(c.id)}
                    >
                      {c.pppoe_user}
                    </TableCell>
                    <TableCell
                      className="font-mono text-muted-foreground"
                      onClick={() => gotoContrato(c.id)}
                    >
                      {c.ip_asignada}
                    </TableCell>
                    <TableCell onClick={() => gotoContrato(c.id)}>
                      Día {c.fecha_corte}
                    </TableCell>
                    <TableCell onClick={() => gotoContrato(c.id)}>
                      <Badge variant={ESTATUS_VARIANT[c.estatus]}>{c.estatus}</Badge>
                    </TableCell>
                    <TableCell onClick={() => gotoContrato(c.id)}>
                      {c.equipo_modalidad === "comodato" ? (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/40"
                          title="ISP recupera al cancelar"
                        >
                          Comodato
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/40"
                          title="Pagado por el cliente"
                        >
                          Propiedad
                        </span>
                      )}
                    </TableCell>
                    <TableCell onClick={() => gotoContrato(c.id)}>
                      {formatDate(c.created_at)}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {canSuspend && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setActionContrato({
                              id: c.id,
                              codigo: c.codigo,
                              action: "suspender",
                            })
                          }
                        >
                          <Pause size={12} /> Suspender
                        </Button>
                      )}
                      {canReactivate && (
                        <Button
                          size="sm"
                          onClick={() =>
                            setActionContrato({
                              id: c.id,
                              codigo: c.codigo,
                              action: "reactivar",
                            })
                          }
                        >
                          <Play size={12} /> Activar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      )}

      <ContratoFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        clientePreselectId={clienteId}
      />

      {actionContrato && (
        <RowActionDialog
          clienteId={clienteId}
          contratoId={actionContrato.id}
          codigo={actionContrato.codigo}
          action={actionContrato.action}
          onClose={() => setActionContrato(null)}
        />
      )}
    </div>
  );
}

/**
 * Wrapper que instancia los hooks de suspender/reactivar para la fila activa.
 * Separado porque los hooks reciben el id en el argumento — un hook por fila no es viable.
 */
function RowActionDialog({
  clienteId,
  contratoId,
  codigo,
  action,
  onClose,
}: {
  clienteId: string;
  contratoId: string;
  codigo: string;
  action: "suspender" | "reactivar";
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const suspenderMut = useSuspenderContrato(contratoId);
  const reactivarMut = useReactivarContrato(contratoId);
  const mut = action === "suspender" ? suspenderMut : reactivarMut;

  return (
    <ActionDialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={
        action === "suspender"
          ? `Suspender contrato ${codigo}`
          : `Reactivar contrato ${codigo}`
      }
      description={
        action === "suspender"
          ? "Se deshabilita el PPPoE en el router. El cliente pierde acceso hasta reactivar."
          : "Se habilita el PPPoE en el router. El cliente recupera acceso."
      }
      actionLabel={action === "suspender" ? "Suspender" : "Reactivar"}
      isPending={mut.isPending}
      motivoRequired={false}
      onConfirm={async (motivo) => {
        try {
          const res = await mut.mutateAsync(motivo);
          // Invalida resumen del cliente para refrescar badges/strip
          qc.invalidateQueries({ queryKey: ["clientes", "resumen", clienteId] });
          onClose();
          if (res.job_id) {
            // Toast se maneja en Sonner via import lazy
            const { toast } = await import("sonner");
            toast.info(
              action === "suspender" ? "Suspensión encolada" : "Reactivación encolada",
              { description: "Aplicándose al router…" },
            );
          }
        } catch (e) {
          return (e as ApiError).message;
        }
      }}
    />
  );
}
